// Services
import api from '../../api'

// Utilities
import CreditCard from '../../utility/creditCardUtil'
import isEmpty from '../../utility/isEmpty'
import Language from '../../utility/languageUtil'
import Logger from '../../utility/loggerUtil'

import { Cards } from '../../constants'

export default {
  async getPayDebitCards (context) {
    try {
      const paymentProcessor = context.rootGetters['loans/getLoanPaymentProcessorType']
      const response = await api.paymentMethod.getDebitCards(paymentProcessor)

      const tempCards = response.cards
      const validations = response.validations

      if (validations !== undefined) {
        context.commit('getPayDebitCardsSuccess', { validations })
      }

      if (isEmpty(tempCards)) {
        context.commit('getPayDebitCardsSuccess', { cards: [] })
        return
      }

      const cards = CreditCard.formatCards(tempCards)

      Logger.info('Formatted cards')
      Logger.debug(cards)

      context.commit('getPayDebitCardsSuccess', { cards })
    } catch (error) {
      Logger.error('Error getting cards')
      Logger.debug(error)

      throw new Error(`${Language.$t('global.get_cards_query_error')}: ${error.message}`)
    }
  },

  async addPayDebitCard (context, cardData) {
    try {
      const isGuestPayment = context.rootState.payment.guestPayment || false
      const saveAsGuestCard = !isEmpty(cardData.card.savePaymentMethod) ? !cardData.card.savePaymentMethod : false
      const isGuestCard = !cardData.isOrigination && (isGuestPayment || saveAsGuestCard)

      let cardResponse

      if (context.rootState.session.canRepayCardCreate) {
        const repayTokenInfo = context.state.repayTokenInfo
        if (isEmpty(repayTokenInfo)) {
          throw new Error('Please confirm card information is entered correctly.')
        }

        const expirationMonth = repayTokenInfo.expiration.substring(0, 2)
        const expirationYear = repayTokenInfo.expiration.substring(2)

        cardResponse = await api.paymentMethod.addRepayDebitCard(repayTokenInfo.name, repayTokenInfo.token, repayTokenInfo.cardNumber, repayTokenInfo.cardType, expirationMonth, expirationYear, isGuestCard, repayTokenInfo.cardBin)
      } else if (context.rootState.session.canUnitasCardCreate) {
        const [expirationMonth, expirationYear] = cardData.card.expiration.split('/')
        cardResponse = await api.paymentMethod.addDebitCard(cardData.card.cardNumber, expirationMonth, expirationYear, isGuestCard)
      } else {
        const tokenPayTempToken = context.state.bridgePayToken // tokenPay is the BridgePay product we use to get a temp token (which we then exchange for a permanent token)
        if (isEmpty(tokenPayTempToken)) {
          throw new Error('Please confirm card information is entered correctly.') // Open ended message, since we do not know specifically where BridgePay fails
        }

        cardResponse = await api.paymentMethod.addTokenizedDebitCard(tokenPayTempToken, isGuestCard)
        context.commit('setTempBridgePayToken', undefined)
      }

      const cardObj = {
        cardToken: cardResponse.card.cardToken,
        cardIds: cardResponse.card.cardIds,
        cardNumber: `•••• •••• •••• ${cardResponse.card.cardNumber}`,
        cardType: cardResponse.card.cardType,
        cardholderName: cardData.card.name,
        customerId: context.rootState.customer.customerId,
        customerIds: cardResponse.card.customerIds,
        expirationMonth: cardResponse.card.expirationMonth,
        expirationYear: cardResponse.card.expirationYear,
        guestCard: cardResponse.card.guestCard,
        newCard: true,
        canDisburse: cardResponse.card.canDisburse
      }

      const formattedCard = CreditCard.formatCard(cardObj)

      context.commit('addPayDebitCardSuccess', formattedCard)

      const isDisburseEligibleCard = cardData.isDisburse || cardData.isOrigination

      if (isDisburseEligibleCard && cardObj.canDisburse) {
        context.commit('payment/setDisburseCard', {
          cardIds: cardObj.cardIds,
          cardHolderName: cardObj.cardholderName,
          cardNumber: cardObj.cardNumber,
          cardType: cardObj.cardType
        }, { root: true })
      }

      if (cardData.saveToPaymentStore) {
        context.commit('payment/setPaymentData', {
          paymentCardToken: cardResponse.card.cardToken,
          paymentCardIds: cardResponse.card.cardIds,
          cardHolderName: cardData.card.name,
          cardNumber: cardResponse.card.cardNumber,
          cardType: formattedCard.cardType,
          guestCard: cardResponse.card.guestCard,
          saveGuestCard: isGuestPayment && !saveAsGuestCard, // Currently only saving guest cards after payment is completed in a guest payment flow
          expirationMonth: formattedCard.expirationMonth,
          expirationYear: formattedCard.expirationYear
        }, { root: true })
      }

      if (isDisburseEligibleCard && !cardObj.canDisburse) { // If a card is added as part of disburse flow but comes back not eligible throw error (but card is still saved to customer account as payment eligible only)
        throw new Error(Language.$t('global.card_invalid_error'))
      }

      return cardObj
    } catch (e) {
      let serviceCallError

      if (e.message === 'InternalErrorException') {
        serviceCallError = Language.$t('global.server_error_retry')
      } else if (e.message === 'TooManyRequestsException' || e.message === 'LimitExceededException') {
        serviceCallError = Language.$t('global.add_card_too_many_requests_error')
      } else if (e.message === 'AccessDeniedException') {
        serviceCallError = Language.$t('global.add_card_access_denied_error')
      } else if (e.message === 'ServiceUnavailableException') {
        serviceCallError = Language.$t('global.add_card_service_unavailable_error')
      } else if (e.message === 'RequestTimeoutException') {
        serviceCallError = Language.$t('global.add_card_request_timeout_error')
      } else if (e.response && e.response.data && e.response.data.error) {
        serviceCallError = e.response.data.error
      } else {
        serviceCallError = `${Language.$t('global.add_card_unknown_error')} ${e.message}${e.message.endsWith('.') ? '' : '.'}`
      }

      throw new Error(serviceCallError)
    }
  },

  async updateDebitCard (context, { cardIds, isGuestCard }) {
    if (isGuestCard) {
      Logger.debug('Adding guest card flag is not supported, only removing') // Add it yourself if you want it, we can't do everything for you!
      return
    }

    try {
      const card = context.getters.getCardById(cardIds)

      if (isEmpty(card) || !card.guestCard) {
        Logger.debug('No card found or card is not guest')
        return
      }

      await api.paymentMethod.updateDebitCard(cardIds, isGuestCard)

      context.commit('updateCard', { cardIds, isGuestCard })
      context.commit('payment/resetGuestCard', { cardIds }, { root: true }) // Clear from payment store if its already been selected
    } catch (error) {
      Logger.error('Error updating card')
      Logger.debug(error)
      throw error
    }
  },

  /**
   * Gets the secure acceptance session data.
   * @returns {Promise<{acceptance: Object, acceptanceConfig: {formUrl: String}}>} The secure acceptance form data.
   */
  async getSecureAcceptData (context) {
    try {
      const storeId = !isEmpty(context.rootState.payment.loan?.storeId) ? context.rootState.payment.loan.storeId : context.rootState.customer.customerStoreId
      const result = await context.dispatch('getCardFormKey', {
        storeId,
        transactionType: Cards.transactionType.disbursement.id
      })

      return result
    } catch (e) {
      let serviceCallError

      if (e.response && e.response.data && e.response.data.error && e.response.data.error.length > 0) {
        serviceCallError = e.response.data.error
      } else {
        serviceCallError = `${Language.$t('global.add_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  async removePayDebitCard (context, cardData) {
    try {
      await api.paymentMethod.removeDebitCard(cardData.cardIds)

      context.commit('removePayDebitCardSuccess', { cardIds: cardData.cardIds })

      if (cardData.isDisburse) {
        context.commit('payment/clearDisburseCard', undefined, { root: true }) // Clear from payment store if its already been selected
      }
    } catch (e) {
      let serviceCallError

      if (e.response && e.response.data && e.response.data.error) {
        serviceCallError = `${Language.$t('global.remove_card_unknown_error')} ${e.response.data.error}`
      } else if (e.message === 'InternalErrorException') {
        serviceCallError = Language.$t('global.server_error_retry')
      } else if (e.message === 'TooManyRequestsException' || e.message === 'LimitExceededException') {
        serviceCallError = Language.$t('global.remove_card_too_many_requests_error')
      } else if (e.message === 'AccessDeniedException') {
        serviceCallError = Language.$t('global.remove_card_access_denied_error')
      } else if (e.message === 'ServiceUnavailableException') {
        serviceCallError = Language.$t('global.remove_card_service_unavailable_error')
      } else if (e.message === 'RequestTimeoutException') {
        serviceCallError = Language.$t('global.remove_card_request_timeout_error')
      } else {
        serviceCallError = `${Language.$t('global.remove_card_unknown_error')} ${e.message}.`
      }

      throw new Error(serviceCallError)
    }
  },

  async getCardFormKey (context, data) {
    try {
      const origin = window.location.origin
      return api.paymentMethod.getCardFormKey(data.transactionType, data.storeId, origin)
    } catch (e) {
      Logger.error('Error getting card form key')
      Logger.debug(e)
      throw e
    }
  }
}
